import { domAnimation, LazyMotion, m } from "framer-motion";
import React from "react";
import { useNavigate } from "react-router-dom";

import useIsLoggedIn from "@/app/hooks/use-IsLoggedIn";
import { Button } from "@/app/Views/JobsPage/components/Button";
import Header from "@/app/Views/Profile/components/Header";
import Footer from "../Footer";

/* ------------------------------------------------------------------ */
/*  Animation variants                                                 */
/* ------------------------------------------------------------------ */

const EASE = [0.22, 1, 0.36, 1] as [number, number, number, number];

const fadeUp = {
	hidden: { opacity: 0, y: 24 },
	show: {
		opacity: 1,
		y: 0,
		transition: { duration: 0.6, ease: EASE },
	},
};

const stagger = {
	hidden: { opacity: 0 },
	show: {
		opacity: 1,
		transition: { staggerChildren: 0.12, delayChildren: 0.08 },
	},
};

/* ------------------------------------------------------------------ */
/*  Content data                                                       */
/* ------------------------------------------------------------------ */

const seekerPoints = [
	{
		title: "Tailored Recommendations",
		text: "Receive job opportunities that match your skills and career goals — no more endless scrolling.",
	},
	{
		title: "One-Click Applications",
		text: "Apply quickly without tedious forms, so you never miss out on the right opportunity.",
	},
	{
		title: "Personalized Feedback",
		text: "Get actionable insights on your resume that help you stand out to employers.",
	},
];

const employerPoints = [
	{
		title: "Smart Candidate Matching",
		text: "Instantly identify top candidates — cutting manual resume screening time by hours.",
	},
	{
		title: "Streamlined Hiring",
		text: "Automate repetitive tasks like shortlisting and screening so your team focuses on decisions that matter.",
	},
	{
		title: "Real-Time Collaboration",
		text: "A centralized dashboard that keeps your hiring team aligned, eliminating endless email threads.",
	},
	{
		title: "Data-Driven Insights",
		text: "Make smarter hiring decisions with analytics that reduce costs and improve candidate quality.",
	},
];

const actions = [
	{
		label: "Job Seekers",
		text: "Set your preferences and let Dproz recommend opportunities that match your skills.",
		cta: "Create Your Profile",
		link: "/register",
		external: false,
	},
	{
		label: "Employers",
		text: "Post your jobs where they'll reach qualified candidates across Tanzania — not just your inbox.",
		cta: "Start Hiring",
		link: "https://hiring.dproz.com/signup",
		external: true,
	},
	{
		label: "Everyone",
		text: "See a job that could change someone's life? Share it with us. We'll verify it and connect it to the right people.",
		cta: null,
		link: null,
		external: false,
	},
];

/* ------------------------------------------------------------------ */
/*  Dot grid — reused in hero and CTA backgrounds                     */
/* ------------------------------------------------------------------ */

const dotGridStyle: React.CSSProperties = {
	backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)",
	backgroundSize: "32px 32px",
};

/* ------------------------------------------------------------------ */
/*  Numbered list item (shared between seeker & employer columns)      */
/* ------------------------------------------------------------------ */

function NumberedItem({ index, title, text, delay }: { index: number; title: string; text: string; delay: number }) {
	return (
		<m.li
			className="flex items-baseline gap-5 py-5 border-b border-gray-200 last:border-0"
			initial={{ opacity: 0, y: 14 }}
			whileInView={{ opacity: 1, y: 0 }}
			viewport={{ once: true, margin: "-40px" }}
			transition={{ duration: 0.5, delay, ease: EASE }}
		>
			<span className="font-plus-jakarta text-xs font-bold text-blue-700 tabular-nums shrink-0">0{index + 1}</span>
			<div>
				<span className="block font-inter text-[15px] font-semibold text-gray-900 mb-1">{title}</span>
				<span className="block font-inter text-sm text-gray-500 leading-relaxed">{text}</span>
			</div>
		</m.li>
	);
}

/* ------------------------------------------------------------------ */
/*  Section label (the ——— LABEL pattern)                              */
/* ------------------------------------------------------------------ */

function SectionLabel({ label, trailing = false }: { label: string; trailing?: boolean }) {
	return (
		<div className="flex items-center gap-3 mb-8">
			{!trailing && <span className="h-[1.5px] w-10 bg-blue-700" />}
			<span className="font-inter text-xs font-semibold text-blue-700 tracking-[0.2em] uppercase">{label}</span>
			{trailing && <span className="h-[1.5px] flex-1 bg-gray-200" />}
		</div>
	);
}

/* ================================================================== */
/*  Page                                                               */
/* ================================================================== */

function AboutUs() {
	const navigate = useNavigate();
	const isLoggedIn = useIsLoggedIn();

	return (
		<LazyMotion features={domAnimation} strict>
			<div className="min-h-screen bg-white">
				<Header isLoggedIn={isLoggedIn} />

				<main>
					{/* ── Section 1: Opening Statement ──────────────────── */}
					<section className="px-2 sm:px-4 pt-4">
						<div className="relative bg-blue-800 rounded-2xl overflow-hidden">
							<div className="absolute inset-0 opacity-[0.03]" style={dotGridStyle} />
							<div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(37,99,235,0.2)_0%,transparent_60%)]" />

							<m.div
								className="relative max-w-6xl mx-auto px-6 sm:px-10 lg:px-16 py-20 sm:py-28 lg:py-36"
								variants={stagger}
								initial="hidden"
								animate="show"
							>
								<m.p
									className="font-inter text-xs font-semibold text-blue-200/70 tracking-[0.2em] uppercase mb-8"
									variants={fadeUp}
								>
									About Dproz
								</m.p>

								<m.h1
									className="font-plus-jakarta !text-[clamp(2rem,5.5vw,4rem)] !font-extrabold !text-white !leading-[1.08] !tracking-tight !max-w-3xl !mb-8"
									variants={fadeUp}
								>
									Every Tanzanian deserves a fair chance to build a good career.
								</m.h1>

								<m.div className="max-w-xl space-y-5" variants={fadeUp}>
									<p className="font-inter text-base sm:text-lg text-white/65 leading-relaxed">
										Dproz is more than a job platform. It is a movement for opportunity. We believe it should not matter
										where you come from — what matters is that you are ready to learn, ready to grow, and ready to
										contribute.
									</p>
									<p className="font-inter text-base sm:text-lg text-white/65 leading-relaxed">
										But too many people never see the right job. Job information is hidden in private circles or buried
										on platforms that are hard to access. We are here to change that.
									</p>
								</m.div>
							</m.div>
						</div>
					</section>

					{/* ── Section 2: Problem & Mission ──────────────────── */}
					<section className="py-20 sm:py-28">
						<m.div
							className="max-w-6xl mx-auto px-6 sm:px-10 lg:px-16"
							initial={{ opacity: 0, y: 24 }}
							whileInView={{ opacity: 1, y: 0 }}
							viewport={{ once: true, margin: "-80px" }}
							transition={{ duration: 0.7, ease: EASE }}
						>
							<div className="grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-24">
								{/* Left: The Problem */}
								<div className="lg:col-span-5">
									<SectionLabel label="The problem" />
									<h2 className="font-plus-jakarta text-2xl sm:text-3xl font-bold text-gray-900 leading-tight tracking-tight mb-6">
										Access to opportunity is still unequal.
									</h2>
									<div className="font-inter text-base text-gray-500 leading-relaxed space-y-4 max-w-lg">
										<p>
											When job information is hard to reach, candidates miss out on roles they are qualified for.
											Employers struggle to find the right talent. Our country loses potential that could create real
											value.
										</p>
										<p>
											That is why Dproz exists — not just as a platform, but as a growing movement to make sure no
											opportunity is wasted.
										</p>
									</div>
								</div>

								{/* Right: Our Mission */}
								<div className="lg:col-span-7 lg:pt-2">
									<SectionLabel label="Our mission" trailing />
									<h2 className="font-plus-jakarta text-2xl sm:text-3xl font-bold text-gray-900 leading-tight tracking-tight mb-6">
										To make job opportunities easy to find and open to everyone, in every part of Tanzania.
									</h2>
									<div className="font-inter text-base text-gray-500 leading-relaxed space-y-4 max-w-lg">
										<p>
											We are building tools that support both sides of the hiring equation. If you are a job seeker,
											Dproz helps you find jobs that match your skills and interests. If you are an employer, Dproz
											helps you find the right people faster and easier.
										</p>
										<p>
											We are also building a community of Dependable Professionals — people who show up, do the work,
											and help each other grow.
										</p>
									</div>
								</div>
							</div>
						</m.div>
					</section>

					{/* ── Section 3: How We Help ────────────────────────── */}
					<section className="py-20 sm:py-28 bg-gray-50">
						<div className="max-w-6xl mx-auto px-6 sm:px-10 lg:px-16">
							<m.div
								className="flex items-center gap-3 mb-16"
								initial={{ opacity: 0 }}
								whileInView={{ opacity: 1 }}
								viewport={{ once: true, margin: "-40px" }}
								transition={{ duration: 0.5 }}
							>
								<span className="h-[1.5px] w-10 bg-blue-700" />
								<span className="font-inter text-xs font-semibold text-blue-700 tracking-[0.2em] uppercase">
									How we help
								</span>
								<span className="h-[1.5px] flex-1 bg-gray-200" />
							</m.div>

							<div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
								{/* Job Seekers column */}
								<div>
									<m.h3
										className="font-plus-jakarta text-xl sm:text-2xl font-bold text-gray-900 tracking-tight mb-10"
										initial={{ opacity: 0, y: 16 }}
										whileInView={{ opacity: 1, y: 0 }}
										viewport={{ once: true, margin: "-40px" }}
										transition={{ duration: 0.5, ease: EASE }}
									>
										For Job Seekers
									</m.h3>
									<ul className="space-y-0">
										{seekerPoints.map((point, i) => (
											<NumberedItem
												key={point.title}
												index={i}
												title={point.title}
												text={point.text}
												delay={0.1 + i * 0.08}
											/>
										))}
									</ul>
								</div>

								{/* Employers column */}
								<div>
									<m.h3
										className="font-plus-jakarta text-xl sm:text-2xl font-bold text-gray-900 tracking-tight mb-10"
										initial={{ opacity: 0, y: 16 }}
										whileInView={{ opacity: 1, y: 0 }}
										viewport={{ once: true, margin: "-40px" }}
										transition={{ duration: 0.5, delay: 0.1, ease: EASE }}
									>
										For Employers
									</m.h3>
									<ul className="space-y-0">
										{employerPoints.map((point, i) => (
											<NumberedItem
												key={point.title}
												index={i}
												title={point.title}
												text={point.text}
												delay={0.15 + i * 0.08}
											/>
										))}
									</ul>
								</div>
							</div>
						</div>
					</section>

					{/* ── Section 4: Vision ─────────────────────────────── */}
					<section className="py-24 sm:py-36">
						<m.div
							className="max-w-6xl mx-auto px-6 sm:px-10 lg:px-16"
							initial={{ opacity: 0, y: 24 }}
							whileInView={{ opacity: 1, y: 0 }}
							viewport={{ once: true, margin: "-80px" }}
							transition={{ duration: 0.7, ease: EASE }}
						>
							<SectionLabel label="Our vision" />
							<h2 className="font-plus-jakarta text-[clamp(1.75rem,4vw,3.25rem)] font-bold text-blue-800 leading-[1.15] tracking-tight max-w-4xl">
								To become Tanzania's leading job destination — by making recruitment fast, transparent, and fair for
								everyone.
							</h2>
						</m.div>
					</section>

					{/* ── Section 5: Take Action ────────────────────────── */}
					<section className="px-2 sm:px-4 pb-4">
						<m.div
							className="relative bg-blue-800 rounded-2xl overflow-hidden py-20 sm:py-24"
							initial={{ opacity: 0, y: 24 }}
							whileInView={{ opacity: 1, y: 0 }}
							viewport={{ once: true, margin: "-64px" }}
							transition={{ duration: 0.6, ease: EASE }}
						>
							<div className="absolute inset-0 opacity-[0.03]" style={dotGridStyle} />

							<div className="relative z-10 max-w-6xl mx-auto px-6 sm:px-10 lg:px-16">
								<div className="mb-16">
									<h2 className="font-plus-jakarta text-[clamp(1.75rem,4.5vw,3rem)] font-bold text-white tracking-tight leading-tight mb-4">
										What you can do today
									</h2>
									<p className="font-inter text-lg text-white/60 max-w-xl">
										Whether you are looking for work, looking for talent, or just want to help — there is a place for
										you here.
									</p>
								</div>

								<div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12">
									{actions.map((action, i) => (
										<m.div
											key={action.label}
											className="flex flex-col"
											initial={{ opacity: 0, y: 20 }}
											whileInView={{ opacity: 1, y: 0 }}
											viewport={{ once: true, margin: "-40px" }}
											transition={{
												duration: 0.5,
												delay: i * 0.1,
												ease: EASE,
											}}
										>
											<span className="font-plus-jakarta text-xs font-bold text-blue-300/50 tabular-nums mb-4">
												0{i + 1}
											</span>
											<h3 className="font-plus-jakarta text-lg font-bold text-white mb-3">{action.label}</h3>
											<p className="font-inter text-sm text-white/55 leading-relaxed mb-6 flex-1">{action.text}</p>
											{action.cta && (
												<Button
													className="bg-white text-blue-800 hover:bg-blue-50 rounded-lg w-full sm:w-auto"
													onClick={() => {
														if (action.external) {
															window.open(action.link!, "_blank");
														} else {
															navigate(action.link!);
														}
													}}
												>
													{action.cta}
												</Button>
											)}
										</m.div>
									))}
								</div>

								<div className="mt-16 pt-10 border-t border-white/10">
									<p className="font-inter text-sm text-white/40 max-w-md">
										One step at a time. One opportunity at a time.
									</p>
								</div>
							</div>
						</m.div>
					</section>
				</main>

				<Footer />
			</div>
		</LazyMotion>
	);
}

export default AboutUs;
